import { type CapacityWaitData, capacityWaitOfPart } from '@oci/shared';
import type { UIMessage } from 'ai';
import { Hourglass, Square } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { useMediaQuery } from '~/hooks/use-media-query';
import { translate, useT } from '~/lib/i18n';
import { cn } from '~/lib/utils';

/**
 * A reply waiting for its model's provider (v0.11): OCI keeps below the
 * provider's rate limits and queues turns fairly instead of failing them.
 * The latest `data-capacity` part says where the turn stands.
 */
export function capacityWaitOf(message: UIMessage): CapacityWaitData | null {
  if (message.role !== 'assistant') return null;
  for (let index = message.parts.length - 1; index >= 0; index--) {
    const wait = capacityWaitOfPart(message.parts[index]);
    if (wait) return wait;
  }
  return null;
}

/** "about 40 seconds", "about 3 minutes"; null when unknown. */
export function waitEstimate(seconds: number | null): string | null {
  if (seconds === null) return null;
  if (seconds < 10) return translate('chat.capacityWait.estimateFewSeconds');
  if (seconds < 90)
    return translate('chat.capacityWait.estimateSeconds', { count: Math.round(seconds / 5) * 5 });
  return translate('chat.capacityWait.estimateMinutes', { count: Math.round(seconds / 60) });
}

export function capacityWaitLabel(wait: CapacityWaitData): string {
  if (!wait.model) {
    return wait.position === null
      ? translate('chat.capacityWait.waitingForModel')
      : translate('chat.capacityWait.waitingForModelQueued', { position: wait.position });
  }
  return wait.position === null
    ? translate('chat.capacityWait.waitingFor', { model: wait.model })
    : translate('chat.capacityWait.waitingForQueued', {
        model: wait.model,
        position: wait.position,
      });
}

/** Where the work block will be: the place in the queue, its estimate, and Stop. */
export function CapacityWait({ wait, onStop }: { wait: CapacityWaitData; onStop?: () => void }) {
  const t = useT();
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const estimate = waitEstimate(wait.estimatedWaitSeconds);
  return (
    <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2" data-capacity-wait="">
      <div role="status" aria-live="polite" className="min-w-0">
        <p className="flex items-center gap-2 text-[0.8125rem] font-medium text-[var(--text-primary)]">
          <Hourglass
            aria-hidden="true"
            className={cn('size-4 shrink-0', !reduceMotion && 'motion-safe:animate-pulse')}
          />
          <span className="min-w-0 break-words">{capacityWaitLabel(wait)}</span>
        </p>
        <p className="mt-0.5 ps-6 text-xs text-[var(--text-muted)]">
          {estimate
            ? t('chat.capacityWait.busyWithEstimate', { estimate })
            : t('chat.capacityWait.busy')}
        </p>
      </div>
      {onStop && (
        <Button
          variant="secondary"
          size="sm"
          onClick={onStop}
          aria-label={t('chat.capacityWait.stopWaiting')}
        >
          <Square aria-hidden="true" />
          {t('chat.capacityWait.stop')}
        </Button>
      )}
    </div>
  );
}

function duration(seconds: number): string {
  if (seconds < 90) return translate('chat.capacityWait.durationSeconds', { count: seconds });
  return translate('chat.capacityWait.durationMinutes', { count: Math.round(seconds / 60) });
}

/** What a finished reply says about its wait: how long, or that it ran out. */
export function CapacityNote({ wait }: { wait: CapacityWaitData }) {
  const t = useT();
  if (wait.state === 'admitted' && wait.waitedSeconds >= 5)
    return (
      <p role="note" className="mb-2 text-xs text-[var(--text-muted)]">
        {wait.model
          ? t('chat.capacityWait.waitedFor', {
              duration: duration(wait.waitedSeconds),
              model: wait.model,
            })
          : t('chat.capacityWait.waitedForModel', { duration: duration(wait.waitedSeconds) })}
      </p>
    );
  if (wait.state === 'timeout')
    return (
      <p role="note" className="mb-2 text-xs text-[var(--text-muted)]">
        {wait.model
          ? t('chat.capacityWait.timedOut', { model: wait.model })
          : t('chat.capacityWait.timedOutModel')}
      </p>
    );
  return null;
}
